/**
 * TV / NOC dashboard — designed for helpdesk monitors.
 * - Failed/warning items pinned at top, blinking.
 * - Healthy items auto-scroll/rotate at the bottom.
 * - Big fonts, glanceable.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Logo from "@/components/Logo";
import {
  ArrowsClockwise, ArrowsOut, ArrowsIn, X, HardDrives, Pulse, Bell,
} from "@phosphor-icons/react";

const REFRESH_MS = 15000;
const ROTATE_MS = 6000;
const HEALTHY_PAGE_SIZE = 8;

export default function TVDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const [page, setPage] = useState(0);
  const rotateRef = useRef(null);
  const containerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/tv-data");
      setData(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // categorize
  const { problems, healthy } = useMemo(() => {
    if (!data) return { problems: [], healthy: [] };
    const all = [
      ...(data.backups || []).map(x => ({ ...x, _kind: "backup" })),
      ...(data.monitors || []).map(x => ({ ...x, _kind: "monitor" })),
    ];
    const isProblem = (it) => ["failure", "warning", "missed", "down", "degraded"].includes(it.status);
    return {
      problems: all.filter(isProblem),
      healthy: all.filter((x) => !isProblem(x)),
    };
  }, [data]);

  // auto-rotate healthy section
  useEffect(() => {
    if (rotateRef.current) clearInterval(rotateRef.current);
    if (healthy.length > HEALTHY_PAGE_SIZE) {
      rotateRef.current = setInterval(() => {
        setPage((p) => (p + 1) % Math.max(1, Math.ceil(healthy.length / HEALTHY_PAGE_SIZE)));
      }, ROTATE_MS);
    }
    return () => rotateRef.current && clearInterval(rotateRef.current);
  }, [healthy.length]);

  const onToggleFs = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen?.();
        setFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setFullscreen(false);
      }
    } catch (err) {
      // Fullscreen can fail if triggered outside a user gesture, denied by
      // permission policy, or unsupported in the current browser.
      // Non-fatal — the app remains usable; surface for debugging.
      console.warn("Fullscreen toggle failed:", err);
    }
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const healthyPage = healthy.slice(
    page * HEALTHY_PAGE_SIZE,
    page * HEALTHY_PAGE_SIZE + HEALTHY_PAGE_SIZE,
  );

  return (
    <div ref={containerRef} className="min-h-screen bg-background-deep text-white sw-grid-bg flex flex-col">
      {/* Header */}
      <header className="px-8 py-5 border-b border-border flex items-center justify-between bg-background-deep/90 backdrop-blur">
        <div className="flex items-center gap-6">
          <Logo size={28} />
          <div className="hidden md:flex items-center gap-3 px-3 py-1 border border-brand-cyan/30 rounded-full">
            <span className="status-dot up" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">NOC / monitor wall</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {data && (
            <div className="flex items-center gap-5 text-sm">
              <Stat label="UP/HEALTHY" value={(data.counts.backups.healthy || 0) + (data.counts.monitors.up || 0)} c="text-emerald-400" />
              <Stat label="WARN/DEGRADED" value={(data.counts.backups.warning || 0) + (data.counts.monitors.degraded || 0)} c="text-amber-400" />
              <Stat label="FAIL/DOWN" value={(data.counts.backups.failure || 0) + (data.counts.monitors.down || 0)} c="text-red-400" />
              <Stat label="MISSED" value={data.counts.backups.missed || 0} c="text-slate-300" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="tv-refresh-btn"
              className="p-2 rounded-md border border-border hover:bg-white/5 transition-colors" title="Refresh">
              <ArrowsClockwise size={16} />
            </button>
            <button onClick={onToggleFs} data-testid="tv-fullscreen-btn"
              className="p-2 rounded-md border border-border hover:bg-white/5 transition-colors" title="Toggle fullscreen">
              {fullscreen ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
            </button>
            <button onClick={() => navigate("/app")} data-testid="tv-close-btn"
              className="p-2 rounded-md border border-border hover:bg-white/5 transition-colors" title="Back to dashboard">
              <X size={16} />
            </button>
          </div>
        </div>
      </header>

      {error && <div className="px-8 py-3 text-sm text-red-400 bg-red-500/10 border-b border-red-500/30">{error}</div>}

      {/* Problems (pinned, blinking) */}
      <section className="px-8 pt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <Bell weight="duotone" size={20} className="text-red-400" />
            ATTENTION
            <span className="text-xs text-slate-400 ml-2">
              {problems.length} item{problems.length === 1 ? "" : "s"}
            </span>
          </h2>
          <span className="text-xs text-slate-500">
            updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"}
          </span>
        </div>
        {problems.length === 0 ? (
          <div className="sw-card p-8 text-center">
            <div className="text-emerald-400 text-2xl font-display font-bold">All systems normal.</div>
            <div className="text-xs text-slate-500 mt-1">no failures, no warnings, no missed backups</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tv-problems-grid">
            {problems.map((it) => <TVCard key={`${it._kind}-${it.id}`} item={it} attention />)}
          </div>
        )}
      </section>

      {/* Healthy auto-rotating */}
      <section className="px-8 py-6 flex-1">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
            <Pulse weight="duotone" size={18} className="text-brand-cyan" />
            HEALTHY
            <span className="text-xs text-slate-400 ml-2">
              {healthy.length} item{healthy.length === 1 ? "" : "s"}
              {healthy.length > HEALTHY_PAGE_SIZE && (
                <span className="ml-2 text-brand-cyan">
                  page {page + 1}/{Math.ceil(healthy.length / HEALTHY_PAGE_SIZE)}
                </span>
              )}
            </span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tv-healthy-grid">
          {healthyPage.map((it) => <TVCard key={`${it._kind}-${it.id}`} item={it} />)}
          {healthy.length === 0 && (
            <div className="col-span-full sw-card p-8 text-center text-slate-400">
              No healthy items yet — add monitors or ingest backup reports.
            </div>
          )}
        </div>
      </section>

      <footer className="px-8 py-3 border-t border-border bg-background-deep/80 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 flex items-center justify-between">
        <span>sentrywatch · noc</span>
        <span>refresh {REFRESH_MS / 1000}s · rotate {ROTATE_MS / 1000}s</span>
      </footer>
    </div>
  );
}

function Stat({ label, value, c }) {
  return (
    <div className="flex flex-col items-end">
      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span>
      <span className={`font-display font-bold text-2xl ${c}`}>{value}</span>
    </div>
  );
}

function TVCard({ item, attention }) {
  const isProblem = attention;
  return (
    <div
      className={`sw-card tv-card p-5 ${isProblem ? "border-red-500/60 tv-blink" : "sw-card-hover"}`}
      data-testid={`tv-card-${item._kind}-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {item._kind === "backup" ? (
            <HardDrives size={16} weight="duotone" className="text-brand-cyan shrink-0" />
          ) : (
            <Pulse size={16} weight="duotone" className="text-brand-cyan shrink-0" />
          )}
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{item._kind}</span>
        </div>
        <StatusBadge status={item.status} size="lg" />
      </div>
      <div className="font-display text-xl font-bold tracking-tight mb-1 truncate" title={item.name}>
        {item.name}
      </div>
      <div className="text-xs text-slate-400 truncate">
        {item._kind === "backup"
          ? `${item.vendor || "?"} · ${item.device || "—"}`
          : `${item.type?.toUpperCase()} · ${item.target}`}
      </div>
      {item.client_name && (
        <div className="text-[11px] text-slate-500 truncate mt-1">{item.client_name}</div>
      )}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
        <span className="text-slate-400">
          {item._kind === "backup" && item.last_run_at
            ? new Date(item.last_run_at).toLocaleString()
            : item._kind === "monitor" && item.last_checked_at
              ? `${item.response_ms || "?"}ms · ${new Date(item.last_checked_at).toLocaleTimeString()}`
              : "—"}
        </span>
        {item._kind === "monitor" && item.error && (
          <span className="text-red-400 truncate ml-2" title={item.error}>{item.error.slice(0, 30)}{item.error.length > 30 ? "…" : ""}</span>
        )}
      </div>
    </div>
  );
}
